/**
 * FERP Portal - Entity Type Picker
 *
 * Adaptive selector for entity types within a FERP Solution (module).
 * - First tab is always "Overview" (module dashboard)
 * - ≤12 entity types: Mantine Tabs with icons (smart-ordered)
 * - >12 entity types: Searchable sidebar with "Frequently Used" / "All Types" groups
 *
 * Smart Navigation:
 * - entityTypes array is pre-ordered by useSmartEntityTypes (frequent first)
 * - quickAccessCount indicates how many are in the "top" group
 * - isPersonalized indicates whether ordering is from usage data or curated defaults
 *
 * FERP Principles:
 * - Data-driven: Entity list from SubgraphEntityType[]
 * - Dumb frontend: Just renders what metadata defines
 */

import React, { useState, useMemo } from 'react';
import {
  Tabs,
  Stack,
  TextInput,
  NavLink,
  ScrollArea,
  Badge,
  Text,
  Group,
  Divider,
} from '@mantine/core';
import { IconSearch, IconLayoutDashboard } from '@tabler/icons-react';
import type { SubgraphEntityType } from '@/hooks/useTenantSubgraphConfig';
import { getIcon } from '@/utils/iconMap';

/** Threshold: above this count, switch from tabs to searchable sidebar */
const SIDEBAR_THRESHOLD = 12;

/** Special value for Overview tab */
export const OVERVIEW_TAB = '__overview__';

export interface EntityTypePickerProps {
  entityTypes: SubgraphEntityType[];
  /** null means Overview is selected */
  selectedType: SubgraphEntityType | null;
  onSelect: (entityType: SubgraphEntityType | null) => void;
  /** Whether to show the Overview tab (default: true) */
  showOverview?: boolean;
  /** Smart navigation: number of entity types in the "quick access" section */
  quickAccessCount?: number;
  /** Smart navigation: whether the ordering is personalized vs curated */
  isPersonalized?: boolean;
}

/** Check if an entity type matches a search term */
function matchesSearch(et: SubgraphEntityType, lower: string): boolean {
  return (
    et.name.toLowerCase().includes(lower) ||
    et.pluralName.toLowerCase().includes(lower) ||
    et.description.toLowerCase().includes(lower)
  );
}

/**
 * Tabs view for small entity type counts (≤12).
 * Entity types arrive pre-ordered by smart navigation.
 */
function TabsPicker({
  entityTypes,
  selectedType,
  onSelect,
  showOverview = true,
}: EntityTypePickerProps): React.ReactElement {
  const activeValue = selectedType?.graphqlType ?? (showOverview ? OVERVIEW_TAB : entityTypes[0]?.graphqlType ?? '');

  return (
    <Tabs
      value={activeValue}
      onChange={(value) => {
        if (value === OVERVIEW_TAB) {
          onSelect(null);
          return;
        }
        const found = entityTypes.find((et) => et.graphqlType === value);
        if (found) onSelect(found);
      }}
      variant="outline"
    >
      <Tabs.List>
        {showOverview && (
          <Tabs.Tab
            value={OVERVIEW_TAB}
            leftSection={<IconLayoutDashboard size={16} />}
          >
            Overview
          </Tabs.Tab>
        )}
        {entityTypes.map((et) => {
          const Icon = getIcon(et.icon);
          return (
            <Tabs.Tab
              key={et.graphqlType}
              value={et.graphqlType}
              leftSection={<Icon size={16} />}
            >
              <Group gap={4}>
                {et.pluralName}
                {et.isLifeCritical && (
                  <Badge color="red" variant="filled" size="xs">LC</Badge>
                )}
              </Group>
            </Tabs.Tab>
          );
        })}
      </Tabs.List>
    </Tabs>
  );
}

/** Render a single NavLink for an entity type */
function EntityNavLink({
  et,
  isActive,
  onSelect,
}: Readonly<{
  et: SubgraphEntityType;
  isActive: boolean;
  onSelect: (et: SubgraphEntityType) => void;
}>): React.ReactElement {
  const Icon = getIcon(et.icon);
  return (
    <NavLink
      key={et.graphqlType}
      label={et.pluralName}
      description={et.description}
      leftSection={<Icon size={18} />}
      rightSection={
        et.isLifeCritical ? (
          <Badge color="red" variant="filled" size="xs">LC</Badge>
        ) : undefined
      }
      active={isActive}
      onClick={() => onSelect(et)}
      variant="filled"
    />
  );
}

/**
 * Searchable sidebar for large entity type counts (>12).
 * Groups entity types into "Frequently Used" / "Suggested" and "All Types" sections.
 */
function SidebarPicker({
  entityTypes,
  selectedType,
  onSelect,
  showOverview = true,
  quickAccessCount = 0,
  isPersonalized = false,
}: EntityTypePickerProps): React.ReactElement {
  const [search, setSearch] = useState('');

  // Split entity types into quick access and remaining groups
  const quickAccessTypes = entityTypes.slice(0, quickAccessCount);
  const allTypes = entityTypes.slice(quickAccessCount);

  // Filter both groups by search
  const { filteredQuick, filteredAll } = useMemo(() => {
    if (!search.trim()) {
      return { filteredQuick: quickAccessTypes, filteredAll: allTypes };
    }
    const lower = search.toLowerCase();
    return {
      filteredQuick: quickAccessTypes.filter((et) => matchesSearch(et, lower)),
      filteredAll: allTypes.filter((et) => matchesSearch(et, lower)),
    };
  }, [quickAccessTypes, allTypes, search]);

  const totalFiltered = filteredQuick.length + filteredAll.length;

  return (
    <Stack gap="xs" w={260} style={{ flexShrink: 0 }}>
      {showOverview && (
        <NavLink
          label="Overview"
          leftSection={<IconLayoutDashboard size={18} />}
          active={selectedType === null}
          onClick={() => onSelect(null)}
          variant="filled"
          style={{ fontWeight: 600 }}
        />
      )}
      <TextInput
        placeholder="Search entity types..."
        leftSection={<IconSearch size={16} />}
        value={search}
        onChange={(e) => setSearch(e.currentTarget.value)}
        size="sm"
      />

      {/* Frequently Used / Suggested section */}
      {filteredQuick.length > 0 && (
        <>
          <Text size="xs" c="dimmed" fw={600} tt="uppercase" mt="xs">
            {isPersonalized ? 'Frequently Used' : 'Suggested'}
          </Text>
          <Stack gap={2}>
            {filteredQuick.map((et) => (
              <EntityNavLink
                key={et.graphqlType}
                et={et}
                isActive={selectedType?.graphqlType === et.graphqlType}
                onSelect={onSelect}
              />
            ))}
          </Stack>
        </>
      )}

      {/* Divider between sections */}
      {filteredQuick.length > 0 && filteredAll.length > 0 && <Divider />}

      {/* All Types section */}
      {filteredAll.length > 0 && (
        <>
          <Text size="xs" c="dimmed" fw={600} tt="uppercase">
            All Types ({filteredAll.length})
          </Text>
          <ScrollArea h={400} offsetScrollbars>
            <Stack gap={2}>
              {filteredAll.map((et) => (
                <EntityNavLink
                  key={et.graphqlType}
                  et={et}
                  isActive={selectedType?.graphqlType === et.graphqlType}
                  onSelect={onSelect}
                />
              ))}
            </Stack>
          </ScrollArea>
        </>
      )}

      {/* No quick access, no all types — show flat list (fallback for 0 quickAccessCount) */}
      {quickAccessCount === 0 && (
        <>
          <Text size="xs" c="dimmed">
            {totalFiltered} of {entityTypes.length} types
          </Text>
          <ScrollArea h={500} offsetScrollbars>
            <Stack gap={2}>
              {(search.trim()
                ? entityTypes.filter((et) => matchesSearch(et, search.toLowerCase()))
                : entityTypes
              ).map((et) => (
                <EntityNavLink
                  key={et.graphqlType}
                  et={et}
                  isActive={selectedType?.graphqlType === et.graphqlType}
                  onSelect={onSelect}
                />
              ))}
            </Stack>
          </ScrollArea>
        </>
      )}

      {/* Empty state */}
      {totalFiltered === 0 && search.trim() && (
        <Text size="sm" c="dimmed" ta="center" py="md">
          No entity types match &quot;{search}&quot;
        </Text>
      )}
    </Stack>
  );
}

/**
 * Adaptive entity type picker.
 * Automatically chooses tabs (≤12 types) or searchable sidebar (>12 types).
 */
export function EntityTypePicker(props: EntityTypePickerProps): React.ReactElement {
  if (props.entityTypes.length <= SIDEBAR_THRESHOLD) {
    return <TabsPicker {...props} />;
  }
  return <SidebarPicker {...props} />;
}

export default EntityTypePicker;
